import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/design01/Navigation';
import { 
  Leaf, 
  Award, 
  Heart, 
  Shield, 
  Star,
  ArrowLeft,
  CheckCircle,
  Thermometer,
  Droplets,
  Mountain
} from 'lucide-react';

const CinnamonPage = () => {
  return (
    <>
      {/* Hero Section */}
      <Navigation />
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg" 
            alt="Ceylon Cinnamon bark being harvested"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-orange-900/90 via-amber-800/80 to-orange-700/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <div className="mb-6">
            <Link 
              to="/cinnamon-design-1"
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8 hover:bg-white/20 transition-colors duration-200"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Ceylon Cinnamon
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            The True Cinnamon of Sri Lanka
          </p>
          <p className="text-lg mb-10 opacity-80 max-w-2xl mx-auto leading-relaxed">
            Scientifically known as Cinnamomum verum, Ceylon Cinnamon is the only true cinnamon in the world, prized for its delicate sweetness and remarkable health benefits.
          </p>
        </div>
      </section>

      {/* What Makes It Special */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Makes Ceylon Cinnamon Special?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unlike common cassia cinnamon, Ceylon Cinnamon offers a uniquely sweet, delicate flavor and numerous health advantages
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Leaf className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">True Cinnamon</h3>
              <p className="text-gray-600 leading-relaxed">
                Cinnamomum verum - the only authentic cinnamon species, native to Sri Lanka's unique climate.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Heart className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Low Coumarin</h3>
              <p className="text-gray-600 leading-relaxed">
                Naturally low in coumarin, making it safe for daily consumption and ideal for health-conscious consumers.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Star className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Premium Flavor</h3>
              <p className="text-gray-600 leading-relaxed">
                Delicate, sweet taste with complex aromatic notes that enhance both sweet and savory dishes.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Award className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">World Renowned</h3>
              <p className="text-gray-600 leading-relaxed">
                Globally recognized as the highest quality cinnamon, preferred by chefs and health experts worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cultivation Process */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                From Garden to Your Table
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Our Ceylon Cinnamon comes from carefully tended gardens in Sri Lanka's central highlands, where the perfect combination of climate, soil, and traditional knowledge creates the world's finest cinnamon.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                    <Mountain className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Highland Cultivation</h3>
                    <p className="text-gray-600">Grown in the pristine highlands where altitude and climate create optimal conditions for cinnamon trees.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                    <Droplets className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Traditional Processing</h3>
                    <p className="text-gray-600">Hand-peeled by skilled artisans using centuries-old techniques passed down through generations.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Quality Control</h3>
                    <p className="text-gray-600">Rigorous quality testing ensures only the finest cinnamon reaches international markets.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/4198933/pexels-photo-4198933.jpeg" 
                alt="Traditional cinnamon processing in Sri Lanka"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-4 shadow-xl">
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-700">100%</div>
                  <div className="text-sm text-gray-600">Authentic</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Health Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Remarkable Health Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ceylon Cinnamon is not just a spice - it's a natural wellness enhancer with scientifically proven health benefits
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <Heart className="h-8 w-8 text-red-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Heart Health</h3>
              <p className="text-gray-600 leading-relaxed">
                Supports cardiovascular health by helping to maintain healthy cholesterol levels and promoting good circulation.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <Thermometer className="h-8 w-8 text-blue-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Blood Sugar</h3>
              <p className="text-gray-600 leading-relaxed">
                May help support healthy blood sugar levels and improve insulin sensitivity when used as part of a balanced diet.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <Shield className="h-8 w-8 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Antioxidants</h3>
              <p className="text-gray-600 leading-relaxed">
                Rich in powerful antioxidants that help protect cells from damage and support overall health and wellness.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <Leaf className="h-8 w-8 text-green-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Anti-inflammatory</h3>
              <p className="text-gray-600 leading-relaxed">
                Natural anti-inflammatory properties that may help reduce inflammation and support joint health.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <Star className="h-8 w-8 text-purple-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Brain Function</h3>
              <p className="text-gray-600 leading-relaxed">
                May support cognitive function and brain health through its neuroprotective compounds and antioxidant activity.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <Award className="h-8 w-8 text-amber-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Safe Daily Use</h3>
              <p className="text-gray-600 leading-relaxed">
                Unlike cassia cinnamon, Ceylon Cinnamon is safe for daily consumption due to its naturally low coumarin content.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ceylon vs Cassia Comparison */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ceylon vs. Cassia Cinnamon
            </h2>
            <p className="text-xl text-gray-600">
              Understanding the difference between true cinnamon and common substitutes
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Ceylon Cinnamon */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-amber-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Leaf className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Ceylon Cinnamon</h3>
                <p className="text-amber-700 font-medium">(Cinnamomum verum)</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Sweet, delicate flavor</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Very low coumarin content</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Light brown color</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Thin, paper-like bark layers</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Safe for daily consumption</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Premium medicinal properties</span>
                </div>
              </div>
            </div>

            {/* Cassia Cinnamon */}
            <div className="bg-gray-100 rounded-2xl p-8 shadow-lg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Leaf className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-700">Cassia Cinnamon</h3>
                <p className="text-gray-500 font-medium">(Common Cinnamon)</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="h-5 w-5 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600">Strong, spicy flavor</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-5 w-5 bg-red-400 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600">High coumarin content</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-5 w-5 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600">Dark brown color</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-5 w-5 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600">Thick, hard bark</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-5 w-5 bg-red-400 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600">Limited daily consumption</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-5 w-5 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600">Basic properties</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Uses and Applications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Culinary & Wellness Applications
            </h2>
            <p className="text-xl text-gray-600">
              Versatile uses that enhance both flavor and health
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Culinary Uses</h3>
              <div className="space-y-4">
                <div className="bg-amber-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Baking & Desserts</h4>
                  <p className="text-gray-600 text-sm">Perfect for cinnamon rolls, cookies, cakes, and traditional desserts with its sweet, delicate flavor.</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Beverages</h4>
                  <p className="text-gray-600 text-sm">Enhance teas, coffees, hot chocolate, and smoothies with authentic cinnamon flavor.</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Savory Dishes</h4>
                  <p className="text-gray-600 text-sm">Add depth to curries, rice dishes, and meat marinades with subtle aromatic notes.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Wellness Applications</h3>
              <div className="space-y-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Daily Supplements</h4>
                  <p className="text-gray-600 text-sm">Safe for daily use in capsule form or as a dietary supplement due to low coumarin levels.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Aromatherapy</h4>
                  <p className="text-gray-600 text-sm">Essential oils provide calming and warming properties for therapeutic applications.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Traditional Medicine</h4>
                  <p className="text-gray-600 text-sm">Used in Ayurvedic and traditional medicine systems for various wellness applications.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Ceylon Cinnamon Products
            </h2>
            <p className="text-xl text-gray-600">
              Available in various forms to meet your specific needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <img 
                src="https://images.pexels.com/photos/4198951/pexels-photo-4198951.jpeg" 
                alt="Ceylon Cinnamon sticks"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Cinnamon Sticks</h3>
                <p className="text-gray-600 mb-4">Premium whole cinnamon quills, perfect for brewing teas, flavoring dishes, and decorative presentations.</p>
                <div className="text-sm text-amber-700 font-medium">Available: 5cm, 10cm, 15cm lengths</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <img 
                src="https://images.pexels.com/photos/4198939/pexels-photo-4198939.jpeg" 
                alt="Ground Ceylon Cinnamon powder"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Ground Powder</h3>
                <p className="text-gray-600 mb-4">Finely ground Ceylon Cinnamon powder, ideal for baking, cooking, and supplement applications.</p>
                <div className="text-sm text-amber-700 font-medium">Available: Food grade, Supplement grade</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <img 
                src="https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg" 
                alt="Ceylon Cinnamon essential oil"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Essential Oil</h3>
                <p className="text-gray-600 mb-4">Pure Ceylon Cinnamon leaf oil, extracted through steam distillation for aromatherapy and wellness applications.</p>
                <div className="text-sm text-amber-700 font-medium">Available: 10ml, 30ml, 100ml bottles</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-800 to-orange-800 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Experience Authentic Ceylon Cinnamon?
          </h2>
          <p className="text-xl opacity-90 mb-8 leading-relaxed">
            Partner with Claude's Special for premium Ceylon Cinnamon exports to Australia. Contact us for wholesale pricing and import arrangements.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/#contact"
              className="bg-white text-amber-800 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Contact for Export
            </Link>
            <a 
              href="mailto:exports@claudesspecial.lk"
              className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 backdrop-blur-sm"
            >
              Email Direct
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
                <Leaf className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold">Claude's Special</span>
            </div>
            <p className="text-gray-400 mb-4">
              Authentic Ceylon Cinnamon from Sri Lanka's Highlands
            </p>
            <p className="text-gray-500 text-sm">
              &copy; 2025 Claude's Special. All rights reserved. | Export License: SL-EXP-XXXX
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default CinnamonPage;